import type { FC } from 'react';
import { StyleSheet } from 'react-native';
import { Text } from 'react-native-paper';

import { PastTime } from '@/shared/components/PastTime';
import { getPublicName } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import { dayjs } from '@/time';

type EditorReference = {
  firstName?: string;
  lastName?: string;
};

type EditedHintTranslate = (
  key: 'edited' | 'editedBy' | 'deletedPerson',
  options?: Record<string, unknown>
) => string;

type Props = {
  created?: number | Date;
  lastModified?: number | Date;
  lastEditor?: EditorReference;
  /**
   * Translator with the keys `edited`, `editedBy` and `deletedPerson` in the
   * caller's respective namespace.
   */
  t: EditedHintTranslate;
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    hint: { color: theme.colors.onSurfaceVariant, fontStyle: 'italic' }
  });

/**
 * Shows the edited hint of a post/comment: with the editor's name ("Edited at
 * … by …"), otherwise the plain "edited" hint with relative time. Renders
 * nothing when it was not edited.
 */
export const EditedHint: FC<Props> = ({
  created,
  lastModified,
  lastEditor,
  t
}) => {
  const theme = useAppTheme();
  const styles = makeStyles(theme);

  if (!lastModified || !created || !(lastModified > created)) {
    return <></>;
  }

  return (
    <Text variant="bodySmall" style={styles.hint}>
      {lastEditor ? (
        t('editedBy', {
          time: dayjs(lastModified).format('HH:mm'),
          name:
            getPublicName(lastEditor.firstName, lastEditor.lastName) ||
            t('deletedPerson')
        })
      ) : (
        <>
          {t('edited')}
          <PastTime time={dayjs(lastModified)} />
        </>
      )}
    </Text>
  );
};
